import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { ConfigurationService } from '@sc/data-access-configuration';
import {
  Document,
  DocumentType,
  DocumentTypeEnum,
  Dossier,
  PicturesViewerData,
} from '@sc/data-access-models';
import { InputPictureComponent } from '../input-picture/input-picture.component';
import { NgFor } from '@angular/common';

@Component({
  selector: 'sc-claim-pictures',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./claim-pictures.component.scss'],
  template: `
    <div
      *ngFor="let picture of currentPictures; let index = index"
      [class]="getContainerClass(picture?.type)"
    >
      <sc-input-picture
        [readOnly]="readOnly"
        [picture]="picture"
        [isRequired]="requiredDocuments.includes(picture?.type)"
        (showPicture)="onShowPictures(index)"
        (updatePicture)="onUpdatePicture($event, index)"
      ></sc-input-picture>
    </div>
  `,
  imports: [NgFor, InputPictureComponent],
})
export class ClaimPicturesComponent {
  @Input() readOnly: boolean;
  @Input() set dossier(currentDossier: Dossier) {
    this._dossier = currentDossier;
    this.setCurrentPictures(this._dossier?.claim?.documents);
  }
  @Input() requiredDocuments: DocumentType[] = [];

  @Output() updatePictures = new EventEmitter<Document[]>();
  @Output() showPictures: EventEmitter<PicturesViewerData> =
    new EventEmitter<PicturesViewerData>();

  currentPictures: Document[] = [];
  _dossier: Dossier;

  constructor(private readonly configService: ConfigurationService) {}

  setCurrentPictures(pictures: Document[]) {
    // Decide which picture types can be shown
    this.currentPictures = this.configService
      .getSupportedDocumentTypes()
      .filter((documentType) => {
        switch (documentType) {
          case DocumentTypeEnum.INSURANCE_CARD:
          case DocumentTypeEnum.VEHICLE_REGISTRATION:
            return true;
          case DocumentTypeEnum.DRIVING_LICENSE:
            return this._dossier?.appointment?.isReplacementVehicleSelected;
          case DocumentTypeEnum['3_4']:
          case DocumentTypeEnum.CLOSER_LOOK:
          case DocumentTypeEnum.DOCUMENT:
            return this._dossier?.creationData?.origin !== 'AGENDA';
        }
      })
      .map(
        (docType) =>
          pictures?.find((picture) => picture.type === docType) ||
          this.getNoPictureObject(docType as DocumentType)
      );
  }

  getContainerClass(pictureType: DocumentType) {
    return `picture-container picture-${pictureType}`;
  }

  getNoPictureObject(pictureType: DocumentType): Document {
    return {
      type: pictureType,
      name: null,
      url: null,
      file: null,
      metadata: { name: null, lastModifiedDate: null, mimeType: null },
    };
  }

  onShowPictures(index) {
    this.showPictures.emit({
      pictures: this.currentPictures,
      index: index ?? 0,
    });
  }

  onUpdatePicture(picture: Document, index: number) {
    const pictures = [...this.currentPictures];
    if (!picture) {
      pictures[index] = this.getNoPictureObject(picture.type);
    } else {
      pictures[index] = picture;
    }
    this.updatePictures.emit(pictures);
  }
}
